/**
 * AI Article Assistant Panel for Blog V1
 * Allows admins to generate article content using AI
 */

import React, { useState } from 'react';
import { API_BASE } from '../../config/api';
import { getTitlePlainText } from '../../utils/titleFormat';

const ARTICLE_STYLES = [
  { value: 'study_guide', label: 'Study Guide' },
  { value: 'ranking', label: 'Ranking' },
  { value: 'pathway_explainer', label: 'Pathway Explainer' },
  { value: 'career_pr', label: 'Career PR' },
  { value: 'comparison', label: 'Comparison' },
  { value: 'scholarship', label: 'Scholarship' }
];

export const AIArticleAssistant = ({ 
  onGenerate, 
  existingContentSections = [],
  availableArticles = [] 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [topic, setTopic] = useState('');
  const [articleStyle, setArticleStyle] = useState('');
  const [referenceUrls, setReferenceUrls] = useState(['']);
  const [referenceFiles, setReferenceFiles] = useState([]);
  const [styleReferenceArticle, setStyleReferenceArticle] = useState('');
  const [generationOptions, setGenerationOptions] = useState({
    outline_only: false,
    include_tables: false,
    include_faq: false
  });
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  const handleAddUrl = () => {
    if (referenceUrls.length < 3) {
      setReferenceUrls([...referenceUrls, '']);
    }
  };

  const handleRemoveUrl = (index) => {
    setReferenceUrls(referenceUrls.filter((_, i) => i !== index));
  };

  const handleUrlChange = (index, value) => {
    const newUrls = [...referenceUrls];
    newUrls[index] = value;
    setReferenceUrls(newUrls);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      const sizeMB = file.size / (1024 * 1024);
      return (ext === 'pdf' || ext === 'doc' || ext === 'docx') && sizeMB <= 10;
    });
    setReferenceFiles(validFiles);
  };

  const handleRemoveFile = (index) => {
    setReferenceFiles(referenceFiles.filter((_, i) => i !== index));
  };

  const handleOptionChange = (option) => {
    setGenerationOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  const handleGenerate = async () => {
    if (!topic.trim()) {
      setError('Article Topic is required');
      return;
    }
    if (!articleStyle) {
      setError('Article Style is required');
      return;
    }

    // Check if existing content will be overwritten
    const hasExistingContent = existingContentSections && existingContentSections.length > 0;
    if (hasExistingContent) {
      const confirmOverwrite = window.confirm(
        'This will overwrite existing content sections. Are you sure you want to continue?'
      );
      if (!confirmOverwrite) {
        return;
      }
    }

    setGenerating(true);
    setError(null);

    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        throw new Error('Authentication required');
      }

      // Prepare payload
      const validUrls = referenceUrls.filter(url => url.trim() !== '');
      
      // Upload files if any (files will be uploaded as part of the main request)
      const uploadedFileUrls = [];

      // Get style reference article if selected
      let styleReferenceData = null;
      if (styleReferenceArticle) {
        const article = availableArticles.find(a => a.id === styleReferenceArticle);
        if (article) {
          styleReferenceData = {
            title: getTitlePlainText(article.title),
            content_sections: article.content_sections || []
          };
        }
      }

      const payload = {
        topic: topic.trim(),
        article_style: articleStyle,
        reference_urls: validUrls,
        reference_files: uploadedFileUrls,
        style_reference_article: styleReferenceData,
        generation_options: generationOptions
      };

      const response = await fetch(`${API_BASE}/api/admin/ai/blog-generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        window.location.href = '/admin/login';
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to generate article');
      }

      const data = await response.json();
      
      if (data.success && data.content_sections) {
        if (onGenerate) {
          onGenerate(data);
        }
      } else {
        throw new Error('Invalid response from AI service');
      }
    } catch (err) {
      setError(err.message || 'Failed to generate article. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="pt-6 border-t-2 border-[#E5E7EB]">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center text-left mb-4"
      >
        <h2 className="text-lg font-bold text-[#111827]">AI Article Assistant</h2>
        <svg
          className={`w-5 h-5 text-[#6B7280] transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="space-y-4 bg-[#F9FAFB] rounded-lg p-6">
          {/* Article Topic */}
          <div>
            <label className="block text-sm font-semibold text-[#111827] mb-2">
              Article Topic <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
              placeholder="How to Choose the Right Pre-University Course After SPM & IGCSE"
            />
          </div>

          {/* Article Style */}
          <div>
            <label className="block text-sm font-semibold text-[#111827] mb-2">
              Article Style <span className="text-red-500">*</span>
            </label>
            <select
              value={articleStyle}
              onChange={(e) => setArticleStyle(e.target.value)}
              className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
            >
              <option value="">Select article style</option>
              {ARTICLE_STYLES.map(style => (
                <option key={style.value} value={style.value}>{style.label}</option>
              ))}
            </select>
          </div>

          {/* Reference URLs */}
          <div>
            <label className="block text-sm font-semibold text-[#111827] mb-2">Reference URLs (Optional)</label>
            <p className="text-xs text-[#6B7280] mb-2">Used for structure, coverage, and factual reference only. No copying.</p>
            {referenceUrls.map((url, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="url"
                  value={url}
                  onChange={(e) => handleUrlChange(index, e.target.value)}
                  className="flex-1 px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                  placeholder="https://..."
                />
                {referenceUrls.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveUrl(index)}
                    className="px-3 py-2 text-red-600 hover:text-red-800 font-semibold"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            {referenceUrls.length < 3 && (
              <button
                type="button"
                onClick={handleAddUrl}
                className="text-sm text-[#1E40AF] hover:text-[#1e3a8a] font-semibold"
              >
                + Add URL
              </button>
            )}
          </div>

          {/* Reference Documents */}
          <div>
            <label className="block text-sm font-semibold text-[#111827] mb-2">Reference Documents (Optional)</label>
            <p className="text-xs text-[#6B7280] mb-2">PDF or DOC files. Max 10MB per file.</p>
            <input
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleFileChange}
              multiple
              className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
            />
            {referenceFiles.length > 0 && (
              <div className="mt-2 space-y-1">
                {referenceFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between text-sm text-[#6B7280]">
                    <span>{file.name} ({(file.size / (1024 * 1024)).toFixed(2)}MB)</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Style Reference Article */}
          <div>
            <label className="block text-sm font-semibold text-[#111827] mb-2">Style Reference Article (Optional)</label>
            <p className="text-xs text-[#6B7280] mb-2">Purpose: tone and writing style ONLY</p>
            <select
              value={styleReferenceArticle}
              onChange={(e) => setStyleReferenceArticle(e.target.value)}
              className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
            >
              <option value="">Select an article</option>
              {availableArticles.map(article => (
                <option key={article.id} value={article.id}>
                  {getTitlePlainText(article.title)}
                </option>
              ))}
            </select>
          </div>

          {/* Generation Options */}
          <div>
            <label className="block text-sm font-semibold text-[#111827] mb-2">Generation Options (Optional)</label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={generationOptions.outline_only}
                  onChange={() => handleOptionChange('outline_only')}
                  className="w-4 h-4 text-[#1E40AF] rounded"
                />
                <span className="ml-2 text-sm text-[#374151]">Outline Only</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={generationOptions.include_tables}
                  onChange={() => handleOptionChange('include_tables')}
                  className="w-4 h-4 text-[#1E40AF] rounded"
                />
                <span className="ml-2 text-sm text-[#374151]">Include Tables</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={generationOptions.include_faq}
                  onChange={() => handleOptionChange('include_faq')}
                  className="w-4 h-4 text-[#1E40AF] rounded"
                />
                <span className="ml-2 text-sm text-[#374151]">Include FAQ</span>
              </label>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Generate Button */}
          <button
            type="button"
            onClick={handleGenerate}
            disabled={generating || !topic.trim() || !articleStyle}
            className="w-full px-6 py-3 bg-gradient-to-r from-[#1E40AF] to-[#1e3a8a] hover:shadow-xl text-white font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {generating ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Generating article sections…</span>
              </>
            ) : (
              'Generate with AI'
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default AIArticleAssistant;
